import { useState, React } from "react";
import { Info, ChevronRight } from "lucide-react";
import { GRAPH_NODE_TYPES } from "../GraphUtils";

const FLAT_VAR = "flat";

const updateGraphData = (
  suggestion,
  nodePath,
  discoveryGraphData,
  graphTagType,
  structureType,
) => {
  // Create a deep copy of the discovery graph data
  const newGraphData = JSON.parse(JSON.stringify(discoveryGraphData || {}));

  let currentNode = newGraphData;
  for (let i = 0; i < nodePath?.length - 1; i++) {
    if (!currentNode[nodePath[i]]) {
      currentNode[nodePath[i]] = {};
    }
    currentNode = currentNode[nodePath[i]];
  }

  const cleanedSuggestion = cleanSchema(suggestion);

  // Helper function to ensure value nodes have correct configuration
  const ensureValueNodes = (node, autoSuggestion) => {
    if (!node || typeof node !== "object") return node;

    Object.entries(node).forEach(([key, value]) => {
      // Only process if the key exists in the suggestion
      if (key in autoSuggestion) {
        if (value?.TagAvailableValues) {
          // For flat structure, always set to ['<ANY>']
          // For hierarchical, only set to ['<ANY>'] if no values specified
          if (
            (structureType === FLAT_VAR &&
              (graphTagType === GRAPH_NODE_TYPES.OPEN_ENDED ||
                graphTagType === GRAPH_NODE_TYPES.BINARY)) ||
            !Array.isArray(value.TagAvailableValues) ||
            value.TagAvailableValues.length === 0
          ) {
            node[key] = {
              ...value,
              TagAvailableValues:
                graphTagType === GRAPH_NODE_TYPES.BINARY
                  ? ["Yes", "No"]
                  : ["<ANY>"],
            };
          }
        } else if (typeof value === "object") {
          ensureValueNodes(value, autoSuggestion[key] || {});
        }
      }
    });
    return node;
  };

  if (!nodePath?.length) {
    Object.keys(cleanedSuggestion).forEach((key) => {
      currentNode[key] = cleanedSuggestion[key];
    });
  } else {
    currentNode[nodePath.at(-1)] = cleanedSuggestion;
  }

  // Process the entire graph to ensure value nodes
  ensureValueNodes(newGraphData, cleanedSuggestion);
  return newGraphData;
};

const cleanSchema = (obj) => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }
  const cleanedObj = {};

  for (const [key, value] of Object.entries(obj)) {
    // Skip description and output_type fields
    if (key === "description" || key === "output_type" || key === "maxValues") {
      continue;
    }

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      cleanedObj[key] = cleanSchema(value);
    } else {
      cleanedObj[key] = value;
    }
  }

  return cleanedObj;
};

const PathNavigator = ({ nodePath }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!nodePath?.length) {
    return <span className="text-primary font-semibold">All Data</span>;
  }

  // Create pairs of [tag, value] from the path
  const pathPairs = [];
  for (let i = 0; i < nodePath.length; i += 2) {
    if (nodePath[i] && nodePath[i + 1]) {
      pathPairs.push([nodePath[i], nodePath[i + 1]]);
    }
  }

  // If not expanded, only show the current node
  if (!isExpanded) {
    const currentPair = pathPairs[pathPairs.length - 1];
    return (
      <div className="flex items-center gap-2">
        <span className="text-primary font-semibold">{currentPair[1]}</span>
        <button
          onClick={(e) => {
            e.preventDefault();
            setIsExpanded(true);
          }}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <Info size={12} />
          Show full path
        </button>
      </div>
    );
  }

  // Show full path when expanded
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-gray-500">
        <span>Full path:</span>
        <button
          onClick={() => setIsExpanded(false)}
          className="text-xs hover:text-gray-700"
        >
          Hide
        </button>
      </div>
      <div className="flex items-center flex-wrap gap-2">
        <span className="text-gray-500">All Data</span>
        {pathPairs.map(([tag, value], index) => (
          <div key={index} className="flex items-center gap-2">
            <ChevronRight size={16} className="text-gray-400" />
            <span className="text-gray-600">{tag}:</span>
            <span className="text-primary font-semibold">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export { cleanSchema, PathNavigator, updateGraphData, FLAT_VAR };
